/**
 * FLOOR FRAMING round (docs/MODELER.md) — the wall/roof framing pattern applied to
 * floors: derived joist framing across the floor's boundary, in a settable
 * direction (the instance `joistDirectionDeg`). Joist DEPTH derives from the
 * assembly's structural core thickness exactly like wall studs; plan WIDTH follows
 * the declared family (sawn 1½″ / TJI flange 2½″ — floorCore.ts, the I profile is
 * a recorded deferral). Representation-grade only — no blocking, bridging,
 * hangers, or engineering, ever (the standing wall-framing guard).
 *
 * NOTHING here is an element. Joists derive at generation time from the floor's own
 * boundary + direction — reshape either and it re-frames. Vertically the joist band
 * sits exactly where the assembly's structural core sits (assemblyZonesFt, shared
 * with roof/wall framing): above = finish flooring, below = subfloor/ceiling finish,
 * core = the joist depth. Since a floor's z-extent is flat (level + offset, unlike a
 * sloped roof face), the joist band is two constants, not per-point surfaces.
 *
 * DEFAULT-ON, NO INSTANCE TOGGLE (the wall-framing round's model, not the roof's
 * per-element study): every wood-core floor frames automatically. Visibility AND
 * generation are controlled by the live 3D viewport's SYSTEMS list (FRAMING
 * SESSION Phase 4 — the Floor Framing check, lazily generated) — there is no
 * per-floor flag to fight with it.
 */
import {
  floorJoistWidthIn, isFramedCeilingCoreMaterial, isWoodCoreFloor, seamBeamSize,
  type FramingSplitEntry, type FramingZoneEntry, type Path2D, type Pt2, type ResolvedElement,
} from '@aj/bim-kernel'
import { flattenPath } from '../../flatten'
import { clipperReady, differenceClosed, inflateOpen, intersectClosed, signedArea } from '../../clipper'
import { cutIntoPieces, inPiece, type SlabPiece } from '../slab/pieces'
import type { ElementSolid } from '../wall/generate3d'
import { newMeshAcc, meshAccUvs } from '../roof/meshAcc'
import { assemblyZonesFt, framingMaterialId, prism, sheathingOutsideCoreFt, stations } from '../roof/framing'

const MIN_MEMBER_FT = 1 / 48

/**
 * FRAMING SESSION Phase 3 (docs/MODELER.md "PLATFORM FRAMING + PLATE HEIGHT
 * CORRECTIONS", 2026-08-13) — the PLATFORM BAND of a wood-core framed floor:
 * its plan rings plus the two elevations every platform derivation reads —
 * the JOIST underside (a crossed wall's lower segment caps with double plates
 * directly under it; a bearing stem's top derives to a mudsill under it) and
 * the top of the structural SHEATHING (the subfloor a sole plate rests on —
 * the contiguous substrate/membrane stack above the core; finish layers above
 * that never carry a plate). Null for anything that isn't a real framed floor.
 * Doctrine: BEARING INTENT IS DECLARED BY MODELED OVERLAP — consumers test
 * these rings against their own geometry, no toggles anywhere.
 */
export interface FloorPlatformBand { rings: Pt2[][]; zJoistBot: number; zSheathTop: number }

/** the deck thickness carved from the TOP of the core when no substrate layer
 *  exists above it (0 when the assembly models its sheathing explicitly) */
function deckCarveFt(el: ResolvedElement, zones: { coreFt: number }): number {
  return sheathingOutsideCoreFt(el) > 0 ? 0 : Math.min(0.75 / 12, Math.max(zones.coreFt - MIN_MEMBER_FT, 0))
}

/**
 * FRAMING SESSION Phase 4 — the floor's SHEATHING as its own framing-class solid
 * (groupKey `…|framing-deck`): the Floor Framing system shows JOISTS + this deck
 * but never the finish/mass layers above — a walkable surface in walk mode
 * (collision follows visibility) with the framing visible from below. The deck
 * is the substrate stack above the core when the assembly models one, else the
 * top 3/4″ of the core itself (carved off the joists — see buildFloorFraming).
 * Its top face is exactly floorPlatformBand's zSheathTop, so a platform sole
 * plate visually rests on it.
 */
export function buildFloorSheathing(el: ResolvedElement): ElementSolid | null {
  if (!isWoodCoreFloor(el)) return null
  const boundary = el.params.boundary as Path2D | null
  if (!boundary || !boundary.closed || boundary.nodes.length < 3) return null
  if (el.assemblyThicknessFt <= 0 || !el.level) return null
  const zones = assemblyZonesFt(el)
  if (zones.coreFt < MIN_MEMBER_FT) return null
  const ring = flattenPath(boundary)
  if (ring.length < 3) return null
  const offset = (el.params.heightOffsetFt as number) || 0
  const zCoreTop = el.level.elevationFt + offset - zones.aboveFt
  const sub = sheathingOutsideCoreFt(el)
  const deckFt = sub > 0 ? sub : deckCarveFt(el, zones)
  if (deckFt < 1 / 96) return null
  const zTop = zCoreTop + sub          // = floorPlatformBand's zSheathTop either way
  const a = el.assembly!
  const s = Math.min(Math.max(a.coreBoundary?.start ?? 0, 0), a.layers.length - 1)
  const materialId = (sub > 0 ? a.layers[s - 1]?.materialId : a.layers[s]?.materialId) ?? 'default'
  // FLOOR OPENINGS round: the deck stops at the openings (holes clockwise — the
  // extruder's containment classification reads them as voids)
  const holes = slabHoleRings(el).map((h) => (signedArea(h) <= 0 ? h : [...h].reverse()))
  return {
    elementId: el.id,
    category: 'floor',
    outlines: [signedArea(ring) >= 0 ? ring : [...ring].reverse(), ...holes],
    zBase: zTop - deckFt,
    zTop,
    materialId,
    groupKey: `floor|${el.levelId}|${el.type.assemblyId ?? 'none'}|${el.id}|framing-deck`,
    pieces: [],
    placements: [],
    openingInstances: [],
    skipProjectedOutline: true,
    framing: true,
  }
}

/**
 * DECK DRAINAGE (2026-10-05, Matt: "the plywood is still there, but the decking needs
 * to be able to drain through or it's going to trap water and degrade the sheathing").
 * Where a PLANK finish (open-jointed decking) is painted on a wood-framed floor, the
 * boards bear straight on the joists — no sheathing, nothing above the core under
 * them. This is the joist-top elevation the boards sit on (the core top, less the
 * carved deck when the assembly models its sheathing inside the core lump, since the
 * joists themselves stop there). Null for anything that isn't a framed floor.
 */
export function floorDeckBearingZ(el: ResolvedElement): number | null {
  if (el.category !== 'floor' || !isWoodCoreFloor(el) || !el.level) return null
  const zones = assemblyZonesFt(el)
  if (zones.coreFt < MIN_MEMBER_FT) return null
  const offset = (el.params.heightOffsetFt as number) || 0
  return el.level.elevationFt + offset - zones.aboveFt - deckCarveFt(el, zones)
}

export function floorPlatformBand(el: ResolvedElement): FloorPlatformBand | null {
  if (el.category !== 'floor' || !isWoodCoreFloor(el)) return null
  const boundary = el.params.boundary as Path2D | null
  if (!boundary || !boundary.closed || boundary.nodes.length < 3) return null
  if (el.assemblyThicknessFt <= 0 || !el.level) return null
  const zones = assemblyZonesFt(el)
  if (zones.coreFt < MIN_MEMBER_FT) return null
  const ring = flattenPath(boundary)
  if (ring.length < 3) return null
  const offset = (el.params.heightOffsetFt as number) || 0
  const zCoreTop = el.level.elevationFt + offset - zones.aboveFt
  return {
    rings: [ring],
    zJoistBot: zCoreTop - zones.coreFt,
    zSheathTop: zCoreTop + sheathingOutsideCoreFt(el),
  }
}

/**
 * CEILING CORE DEFINITION round (2026-08-18) — derived CEILING JOISTS for a framed
 * ceiling (kernel ceilingCore.ts: coreType wood/tji; a finish-only ceiling derives
 * nothing). The floor joist field verbatim, in the ceiling's z-model: the assembly
 * extends UP from (level + heightOffset), so the joist band is the core zone
 * measured down from the assembly TOP. No sheathing deck — a ceiling has no
 * walkable subfloor (a lid with a deck is a floor).
 */
export function buildCeilingFraming(el: ResolvedElement): ElementSolid | null {
  if (el.category !== 'ceiling' || !el.assembly) return null
  const m = el.coreMaterial
  if (!m || !isFramedCeilingCoreMaterial(m.name, m.category)) return null
  const coreIdx = Math.min(Math.max(el.assembly.coreBoundary?.start ?? 0, 0), el.assembly.layers.length - 1)
  if (el.assembly.layers[coreIdx]?.function !== 'structure') return null
  if (!clipperReady()) return null
  const boundary = el.params.boundary as Path2D | null
  if (!boundary || !boundary.closed || boundary.nodes.length < 3) return null
  if (el.assemblyThicknessFt <= 0 || !el.level) return null
  const zones = assemblyZonesFt(el)
  if (zones.coreFt < MIN_MEMBER_FT) return null
  const ring = flattenPath(boundary)
  if (ring.length < 3) return null

  const band = ceilingJoistBand(el)
  if (!band) return null
  const { zBot, zTop } = band

  const mesh = joistField(el, ring, zBot, zTop, [])
  if (!mesh) return null
  return {
    elementId: el.id,
    category: 'ceiling',
    outlines: [],
    zBase: zBot,
    zTop,
    materialId: framingMaterialId(el),
    groupKey: `ceiling|${el.levelId}|${el.type.assemblyId ?? 'none'}|${el.id}|framing`,
    pieces: [],
    placements: [],
    openingInstances: [],
    mesh,
    meshOnly: true,
    skipProjectedOutline: true,
    framing: true,
  }
}

/** a floor/ceiling's OPENINGS (Edit Boundary inner loops — the Revit sketch model:
 *  a closed loop drawn inside the outline is a hole), flattened to rings */
export function slabHoleRings(el: ResolvedElement): Pt2[][] {
  return ((el.params.boundaryHoles as Path2D[] | null) || [])
    .filter((h) => h && h.closed && h.nodes?.length >= 3)
    .map((h) => flattenPath(h))
    .filter((r) => r.length >= 3)
}

/** the members of a joist field, as plan rings — split out so the header-out
 *  layout is checkable without building a mesh */
export interface JoistLayout {
  joists: Pt2[][]
  /** doubled full-length joists either side of each framed opening */
  trimmers: Pt2[][]
  /** doubled flush beams across each end of each framed opening, between the trimmers */
  headers: Pt2[][]
}

/**
 * FLOOR OPENINGS round (2026-10-07, Matt: "if a hole is cut out, the framing should
 * create a headout with flush beams naturally") — the joist layout over a boundary
 * WITH openings, the conventional header-out:
 *
 *  - an opening that cuts no joist (a chase that falls between two joists) gets no
 *    header-out at all — nothing was interrupted;
 *  - otherwise the ROUGH OPENING is the hole's bounding box in the joist frame (a
 *    round or angled hole is still framed square — that is how it is built);
 *  - TRIMMERS: a doubled joist on each side of the rough opening, running the full
 *    length like any joist (it carries the headers);
 *  - HEADERS: a doubled flush beam across each end of the rough opening, spanning
 *    trimmer to trimmer, top flush with the joists (same depth — representation-
 *    grade, no sizing);
 *  - TAIL JOISTS: the interrupted joists, running from each header out to bearing.
 *
 * A regular joist that would sit inside a trimmer is dropped (the trimmer replaces
 * it), never left as a sliver beside it. Overlapping/abutting openings are framed
 * each on their own — no merging (recorded deferral).
 */
export function joistLayout(
  ring: Pt2[], holes: Pt2[][], thetaDeg: number, spacingFt: number, w: number,
  /** areas no member may enter (FRAMING ZONES: the flush seam beams' bands) */
  exclude: Pt2[][] = [],
  /**
   * STAIR OPENINGS ACROSS ZONES (2026-10-21, Matt on Grabowski: "the head out for the stairs …
   * the joists just end with no supports"): the floor's openings, decided ONCE for the whole floor
   * (floorOpenings) — when given, these are framed instead of this zone's own holes. A hole that
   * sits against a framing line is a NOTCH in its zone's outline, not a hole, so the zone alone
   * would frame nothing; and its far trimmer belongs in the zone on the other side of the line.
   */
  framedOpenings?: Pt2[][],
): JoistLayout | null {
  const theta = (thetaDeg * Math.PI) / 180
  const d: Pt2 = [Math.cos(theta), Math.sin(theta)]     // joist run direction
  const e: Pt2 = [-d[1], d[0]]                          // spacing axis, perpendicular
  const toE = (p: Pt2) => p[0] * e[0] + p[1] * e[1]
  const toD = (p: Pt2) => p[0] * d[0] + p[1] * d[1]
  const corner = (pe: number, pd: number): Pt2 => [e[0] * pe + d[0] * pd, e[1] * pe + d[1] * pd]
  const rect = (eLo: number, eHi: number, dLo: number, dHi: number): Pt2[] =>
    [corner(eLo, dLo), corner(eHi, dLo), corner(eHi, dHi), corner(eLo, dHi)]

  let e0 = Infinity, e1 = -Infinity, d0 = Infinity, d1 = -Infinity
  for (const p of ring) {
    e0 = Math.min(e0, toE(p)); e1 = Math.max(e1, toE(p))
    d0 = Math.min(d0, toD(p)); d1 = Math.max(d1, toD(p))
  }
  if (!(e1 - e0 > w)) return null

  // the floor itself: outline minus its openings (holes forced clockwise so the
  // NonZero ops read them as voids whichever way they were drawn)
  const outer = signedArea(ring) >= 0 ? ring : [...ring].reverse()
  const holesCw = holes.map((h) => (signedArea(h) <= 0 ? h : [...h].reverse()))
  const region: Pt2[][] = exclude.length ? differenceClosed([outer, ...holesCw], exclude) : [outer, ...holesCw]
  const solidPieces = (rs: Pt2[][]) => rs.filter((r) => r.length >= 3 && signedArea(r) > 1e-9)
  const clipTo = (strip: Pt2[], minus: Pt2[][]): Pt2[][] => {
    let rs = intersectClosed([strip], region)
    if (minus.length && rs.length) rs = differenceClosed(rs, minus)
    return solidPieces(rs)
  }

  const cs = stations(e0 + w / 2, e1 - w / 2, spacingFt)

  // which openings interrupt a joist — those get framed (or the floor-level decision, when given)
  const openings: { he0: number; he1: number; hd0: number; hd1: number }[] = []
  if (framedOpenings) {
    for (const h of framedOpenings) {
      let he0 = Infinity, he1 = -Infinity, hd0 = Infinity, hd1 = -Infinity
      for (const p of h) {
        he0 = Math.min(he0, toE(p)); he1 = Math.max(he1, toE(p))
        hd0 = Math.min(hd0, toD(p)); hd1 = Math.max(hd1, toD(p))
      }
      if (he1 - he0 > 1e-6 && hd1 - hd0 > 1e-6) openings.push({ he0, he1, hd0, hd1 })
    }
  }
  for (const h of framedOpenings ? [] : holes) {
    let he0 = Infinity, he1 = -Infinity, hd0 = Infinity, hd1 = -Infinity
    for (const p of h) {
      he0 = Math.min(he0, toE(p)); he1 = Math.max(he1, toE(p))
      hd0 = Math.min(hd0, toD(p)); hd1 = Math.max(hd1, toD(p))
    }
    if (!(he1 - he0 > 1e-6) || !(hd1 - hd0 > 1e-6)) continue
    const cuts = cs.some((c) => c + w / 2 > he0 && c - w / 2 < he1
      && intersectClosed([rect(c - w / 2, c + w / 2, hd0, hd1)], [h]).some((r) => Math.abs(signedArea(r)) > 1e-6))
    if (cuts) openings.push({ he0, he1, hd0, hd1 })
  }

  const w2 = 2 * w   // doubled members
  const trimmerBands = openings.flatMap((o) => [[o.he0 - w2, o.he0], [o.he1, o.he1 + w2]])
  // the framed box: rough opening plus its two headers — no joist runs through it
  const boxes = openings.map((o) => rect(o.he0, o.he1, o.hd0 - w2, o.hd1 + w2))
  const trimmerStrips = trimmerBands.map(([lo, hi]) => rect(lo, hi, d0 - 1, d1 + 1))

  const out: JoistLayout = { joists: [], trimmers: [], headers: [] }
  for (const c of cs) {
    const lo = c - w / 2, hi = c + w / 2
    // the trimmer replaces any joist it lands on
    if (trimmerBands.some(([a, b]) => hi > a + 1e-6 && lo < b - 1e-6)) continue
    out.joists.push(...clipTo(rect(lo, hi, d0 - 1, d1 + 1), boxes))
  }
  openings.forEach((o, k) => {
    const otherBoxes = boxes.filter((_, j) => j !== k)
    for (const [lo, hi] of trimmerBands.slice(2 * k, 2 * k + 2)) {
      out.trimmers.push(...clipTo(rect(lo, hi, d0 - 1, d1 + 1), otherBoxes))
    }
    for (const [dLo, dHi] of [[o.hd0 - w2, o.hd0], [o.hd1, o.hd1 + w2]]) {
      out.headers.push(...clipTo(rect(o.he0, o.he1, dLo, dHi), trimmerStrips))
    }
  })
  return out
}

/**
 * STAIR OPENINGS ACROSS ZONES (2026-10-21): which of the floor's openings get a header-out, and in
 * which joist direction — decided for the WHOLE floor, not per framing zone. An opening is framed
 * when a zone that reaches it (holds it, or has it as a notch on its outline) has a joist that the
 * opening interrupts — the existing rule (a chase between two joists frames nothing), with that
 * zone's stations. Every zone of the same direction then frames it: the zone the joists come from
 * gets the headers and tails, the zone across a framing line gets the far trimmer.
 */
export function floorOpenings(zones: FramingZone[], holes: Pt2[][], w: number): { ring: Pt2[]; thetaDeg: number }[] {
  const out: { ring: Pt2[]; thetaDeg: number }[] = []
  for (const h of holes) {
    const grown = inflateOpen([...h, h[0]], 0.1, 2)
    for (const z of zones) {
      // does this zone reach the opening?
      const zr = [signedArea(z.outer) >= 0 ? z.outer : [...z.outer].reverse(), ...z.holes.map((r) => (signedArea(r) <= 0 ? r : [...r].reverse()))]
      if (!grown.length || !intersectClosed(grown, zr).some((r) => Math.abs(signedArea(r)) > 1e-4)) continue
      // does it interrupt one of this zone's joists?
      const theta = (z.thetaDeg * Math.PI) / 180
      const d: Pt2 = [Math.cos(theta), Math.sin(theta)], e: Pt2 = [-d[1], d[0]]
      const toE = (p: Pt2) => p[0] * e[0] + p[1] * e[1]
      const toD = (p: Pt2) => p[0] * d[0] + p[1] * d[1]
      let e0 = Infinity, e1 = -Infinity
      for (const p of z.outer) { e0 = Math.min(e0, toE(p)); e1 = Math.max(e1, toE(p)) }
      let he0 = Infinity, he1 = -Infinity, hd0 = Infinity, hd1 = -Infinity
      for (const p of h) { he0 = Math.min(he0, toE(p)); he1 = Math.max(he1, toE(p)); hd0 = Math.min(hd0, toD(p)); hd1 = Math.max(hd1, toD(p)) }
      const cs = stations(e0 + w / 2, e1 - w / 2, z.spacingIn / 12)
      const corner = (pe: number, pd: number): Pt2 => [e[0] * pe + d[0] * pd, e[1] * pe + d[1] * pd]
      const cuts = cs.some((c) => c + w / 2 > he0 && c - w / 2 < he1
        && intersectClosed([[corner(c - w / 2, hd0), corner(c + w / 2, hd0), corner(c + w / 2, hd1), corner(c - w / 2, hd1)]], [h]).some((r) => Math.abs(signedArea(r)) > 1e-6))
      if (cuts) { out.push({ ring: h, thetaDeg: z.thetaDeg }); break }
    }
  }
  return out
}

/**
 * UNCARRIED FLOOR EDGES (2026-10-21, Matt: "where joists run into a floor edge with no wall or beam
 * carrying it (like Santiago's stairwell notches), frame a doubled header there with doubled
 * trimmers, same as a hole"). An edge of a framing zone counts when:
 *  - the joists run INTO it (within 15° of square to them) and it is at least 1′ long,
 *  - it is on the floor's own outline (a hole's edges are floorOpenings', a framing line's are the
 *    seam beam's),
 *  - and less than 70% of it is CARRIED: over a wall whose top meets the joists (bearingCarriers),
 *    over a placed beam at the joists, or against a wall that runs up past the floor (a ledger —
 *    a deck against the house).
 * Each such edge becomes a thin opening just OUTSIDE the floor along it, so joistLayout frames it
 * like the near side of a hole: the doubled header along the edge, the doubled trimmers at its
 * ends (where the floor continues), the joists between them tails into the header. The opening's
 * far side lies outside the floor, so nothing is built there.
 */
export function uncarriedEdgeOpenings(
  zones: FramingZone[], ring: Pt2[], holes: Pt2[][], zBot: number, zTop: number, carriersFrom: ResolvedElement[],
): { ring: Pt2[]; thetaDeg: number }[] {
  const supports: { pts: Pt2[]; reach: number }[] = [...bearingCarriers(zBot, carriersFrom)]
  for (const c of carriersFrom) {
    if (c.category === 'wall') {
      // a wall running up past the floor carries it on a ledger
      const zr = nominalWallZ(c)
      if (!zr || zr.z0 > zBot + 0.05 || zr.z1 < zTop - 0.05) continue
      const path = c.params.path as Path2D | null
      if (!path?.nodes || path.nodes.length < 2) continue
      const pts = flattenPath(path)
      if (pts.length >= 2) supports.push({ pts, reach: Math.max(c.assemblyThicknessFt || 0, 0.3) + 0.1 })
    } else if (c.category === 'beam') {
      const path = c.params.path as Path2D | null
      if (!path?.nodes || path.nodes.length < 2 || !c.level) continue
      const top = c.level.elevationFt + Math.max(Number(c.params.startOffsetFt) || 0, Number(c.params.endOffsetFt) || 0)
      if (top < zBot - 1 || top > zTop + 0.5) continue
      supports.push({ pts: flattenPath(path), reach: (Number(c.params.widthFt) || 0.3) / 2 + 0.1 })
    }
  }
  const onRing = (p: Pt2, r: Pt2[]) => r.some((q, i) => segDist(p, q, r[(i + 1) % r.length]) < 0.02)
  const inside = (p: Pt2, r: Pt2[]) => {
    let c = false
    for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
      const a = r[i], b = r[j]
      if ((a[1] > p[1]) !== (b[1] > p[1]) && p[0] < ((b[0] - a[0]) * (p[1] - a[1])) / (b[1] - a[1]) + a[0]) c = !c
    }
    return c
  }
  const out: { ring: Pt2[]; thetaDeg: number }[] = []
  for (const z of zones) {
    const th = (z.thetaDeg * Math.PI) / 180
    const d: Pt2 = [Math.cos(th), Math.sin(th)]
    const n = z.outer.length
    for (let i = 0; i < n; i++) {
      const a = z.outer[i], b = z.outer[(i + 1) % n]
      const L = Math.hypot(b[0] - a[0], b[1] - a[1])
      if (L < 1) continue
      const u: Pt2 = [(b[0] - a[0]) / L, (b[1] - a[1]) / L]
      if (Math.abs(u[0] * d[0] + u[1] * d[1]) > 0.26) continue          // not square to the joists
      const mid: Pt2 = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]
      if (!onRing(mid, ring) || holes.some((h) => onRing(mid, h))) continue
      // how much of it is carried
      let nS = 0, hit = 0
      const k = Math.max(2, Math.ceil(L / 0.5))
      for (let j = 1; j < k; j++) {
        const p: Pt2 = [a[0] + (b[0] - a[0]) * (j / k), a[1] + (b[1] - a[1]) * (j / k)]
        nS++
        if (supports.some((s) => s.pts.some((q, qi) => qi + 1 < s.pts.length && segDist(p, q, s.pts[qi + 1]) <= s.reach))) hit++
      }
      if (nS && hit / nS >= 0.7) continue
      // the thin opening just outside the floor along the edge
      let nn: Pt2 = [-u[1], u[0]]
      if (inside([mid[0] + nn[0] * 0.05, mid[1] + nn[1] * 0.05], z.outer)) nn = [u[1], -u[0]]
      const o = 0.05
      out.push({ thetaDeg: z.thetaDeg, ring: [a, b, [b[0] + nn[0] * o, b[1] + nn[1] * o], [a[0] + nn[0] * o, a[1] + nn[1] * o]] })
    }
  }
  return out
}

/** the shared joist-field emitter — direction/spacing/width from the element's own
 *  instance + core params (floors and framed ceilings use identical keys); openings
 *  are headed out (joistLayout) */
function joistField(
  el: ResolvedElement, ring: Pt2[], zBot: number, zTop: number, walls: ResolvedElement[],
): { positions: Float32Array; normals: Float32Array; indices: Uint32Array; edges: number[]; uvs?: Float32Array } | null {
  const w = floorJoistWidthIn(el.params) / 12
  const holes = slabHoleRings(el)
  const zones = framingZoneLayout(el, ring, holes)
  // joists stop at a FLUSH beam's face; over a dropped beam or a wall they simply
  // meet at the line (the seam beams themselves are separate, pickable solids —
  // buildSeamBeamSolids)
  const flush = seamBeams(el, ring, holes, w, zBot, zTop, walls).filter((b) => b.kind === 'flush').flatMap((b) => b.rings)

  const framed = [...floorOpenings(zones, holes, w), ...(el.category === 'floor' && walls.length ? uncarriedEdgeOpenings(zones, ring, holes, zBot, zTop, walls) : [])]
  const acc = newMeshAcc()
  const edges: number[] = []
  for (const z of zones) {
    const layout = joistLayout(z.outer, z.holes, z.thetaDeg, z.spacingIn / 12, w, flush, framed.filter((f) => f.thetaDeg === z.thetaDeg).map((f) => f.ring))
    if (!layout) continue
    for (const r of [...layout.joists, ...layout.trimmers, ...layout.headers]) {
      prism(acc, r, () => zBot, () => zTop, edges)
    }
  }
  if (acc.vc === 0) return null
  return {
    positions: new Float32Array(acc.pos),
    normals: new Float32Array(acc.nrm),
    indices: new Uint32Array(acc.idx),
    edges,
    uvs: meshAccUvs(acc),
  }
}

/** the joist band of a framed floor: plan ring + joist bottom/top (top carved for
 *  the deck when the assembly lumps the sheathing into the core) */
export function floorJoistBand(el: ResolvedElement): { ring: Pt2[]; zBot: number; zTop: number } | null {
  if (!isWoodCoreFloor(el)) return null
  const boundary = el.params.boundary as Path2D | null
  if (!boundary || !boundary.closed || boundary.nodes.length < 3) return null
  if (el.assemblyThicknessFt <= 0 || !el.level) return null
  const zones = assemblyZonesFt(el)
  if (zones.coreFt < MIN_MEMBER_FT) return null
  const ring = flattenPath(boundary)
  if (ring.length < 3) return null
  const offset = (el.params.heightOffsetFt as number) || 0
  const zTopFull = el.level.elevationFt + offset
  const zTop = zTopFull - zones.aboveFt - deckCarveFt(el, zones)
  const zBot = zTopFull - zones.aboveFt - zones.coreFt
  return zTop - zBot < MIN_MEMBER_FT ? null : { ring, zBot, zTop }
}

/** the joist band of a framed ceiling (assembly extends UP from level + offset) */
export function ceilingJoistBand(el: ResolvedElement): { ring: Pt2[]; zBot: number; zTop: number } | null {
  if (el.category !== 'ceiling' || !el.assembly || !el.level) return null
  const m = el.coreMaterial
  if (!m || !isFramedCeilingCoreMaterial(m.name, m.category)) return null
  const coreIdx = Math.min(Math.max(el.assembly.coreBoundary?.start ?? 0, 0), el.assembly.layers.length - 1)
  if (el.assembly.layers[coreIdx]?.function !== 'structure') return null
  const boundary = el.params.boundary as Path2D | null
  if (!boundary || !boundary.closed || boundary.nodes.length < 3 || el.assemblyThicknessFt <= 0) return null
  const zones = assemblyZonesFt(el)
  if (zones.coreFt < MIN_MEMBER_FT) return null
  const ring = flattenPath(boundary)
  if (ring.length < 3) return null
  const offset = (el.params.heightOffsetFt as number) || 0
  const zTop = el.level.elevationFt + offset + el.assemblyThicknessFt - zones.aboveFt
  const zBot = zTop - zones.coreFt
  return zTop - zBot < MIN_MEMBER_FT ? null : { ring, zBot, zTop }
}

export function framingSplitsOf(p: Record<string, unknown>): FramingSplitEntry[] {
  return Array.isArray(p.framingSplits) ? (p.framingSplits as FramingSplitEntry[]).filter((x) => x?.pts?.length >= 2) : []
}
export function framingZonesOf(p: Record<string, unknown>): FramingZoneEntry[] {
  return Array.isArray(p.framingZones) ? (p.framingZones as FramingZoneEntry[]).filter((x) => Array.isArray(x?.seed)) : []
}

export interface FramingZone extends SlabPiece {
  thetaDeg: number
  spacingIn: number
  /** the override entry this zone reads, if any */
  zoneId: string | null
  overridden: boolean
}

/**
 * FRAMING ZONES (2026-10-07) — the element's framing lines cut it into zones; each
 * zone takes the LAST override entry whose seed lies in it (the finish-area model),
 * else the element's own direction/spacing. Pure geometry + params: the outline
 * editor calls this too, on the lines being drawn, to show and pick zones.
 */
export function framingZonesFor(
  ring: Pt2[], holes: Pt2[][], splitLines: Pt2[][], zoneEntries: FramingZoneEntry[],
  defaults: { thetaDeg: number; spacingIn: number },
): FramingZone[] {
  const pieces = splitLines.length ? cutIntoPieces(ring, holes, splitLines) : [{ outer: ring, holes }]
  return pieces.map((pc) => {
    let hit: FramingZoneEntry | null = null
    for (const z of zoneEntries) if (inPiece(pc, z.seed)) hit = z
    const th = hit?.joistDirectionDeg
    const sp = hit?.joistSpacingIn
    return {
      ...pc,
      thetaDeg: typeof th === 'number' && Number.isFinite(th) ? th : defaults.thetaDeg,
      spacingIn: typeof sp === 'number' && sp > 0 ? sp : defaults.spacingIn,
      zoneId: hit?.id ?? null,
      overridden: !!hit && (typeof th === 'number' || typeof sp === 'number'),
    }
  })
}

function framingZoneLayout(el: ResolvedElement, ring: Pt2[], holes: Pt2[][]): FramingZone[] {
  return framingZonesFor(ring, holes, framingSplitsOf(el.params).map((x) => x.pts), framingZonesOf(el.params), {
    thetaDeg: Number(el.params.joistDirectionDeg) || 0,
    spacingIn: Number(el.params?.joistSpacingIn) || 16,
  })
}

export interface SeamBeam {
  splitId: string
  /** what the beam resolved to */
  kind: 'flush' | 'dropped' | 'wall'
  /** true when no kind was chosen and the model decided (a bearing wall under the
   *  line → 'wall', else 'flush') */
  auto: boolean
  widthFt: number
  depthFt: number
  /** the SEAM_BEAM_SIZES value in use */
  size: string
  rings: Pt2[][]
  /** member vertical extent */
  zLo: number
  zHi: number
}

/** a wall's NOMINAL vertical extent — wall/generate3d.ts#wallZRange, repeated here
 *  so this module never imports the wall generator (which imports this one) */
function nominalWallZ(w: ResolvedElement): { z0: number; z1: number } | null {
  if (!w.level) return null
  const z0 = w.level.elevationFt + ((w.params.baseOffsetFt as number) || 0)
  const topLevel = w.params.topMode === 'level' ? w.paramLevels?.topLevelId : undefined
  const z1 = topLevel ? topLevel.elevationFt + ((w.params.topOffsetFt as number) || 0) : z0 + ((w.params.unconnectedHeightFt as number) || 9)
  return z1 - z0 > 1e-6 ? { z0, z1 } : null
}

const segDist = (p: Pt2, a: Pt2, b: Pt2): number => {
  const dx = b[0] - a[0], dy = b[1] - a[1], L2 = dx * dx + dy * dy
  const t = L2 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2)) : 0
  return Math.hypot(p[0] - (a[0] + dx * t), p[1] - (a[1] + dy * t))
}

/**
 * AUTO BEARING (2026-10-08, Matt: "let's allow it to be automatic. If it can't be
 * derived from the model, default to providing a beam"): does a WALL below carry
 * this framing line? Yes when walls whose top reaches the joist band (top within
 * 1′ under the joists to 1½′ above them) and whose body starts at least 2′ lower
 * run under at least 70% of the line's length inside the floor. Plan distance is
 * measured to the wall's path with its full thickness as reach, so a wall drawn on
 * either face (location line) still counts. Anything less → a beam.
 */
export function bearingWallUnder(line: Pt2[], inFloor: (p: Pt2) => boolean, zJoistBot: number, walls: ResolvedElement[]): boolean {
  const carriers = bearingCarriers(zJoistBot, walls)
  if (!carriers.length) return false
  let n = 0, hit = 0
  for (const p of lineSamples(line)) {
    if (!inFloor(p)) continue
    n++
    if (carriers.some((c) => c.pts.some((q, qi) => qi + 1 < c.pts.length && segDist(p, q, c.pts[qi + 1]) <= c.reach))) hit++
  }
  return n > 0 && hit / n >= 0.7
}

/** points every 6″ along a polyline (ends included) */
function lineSamples(line: Pt2[]): Pt2[] {
  const out: Pt2[] = []
  for (let i = 0; i + 1 < line.length; i++) {
    const a = line[i], b = line[i + 1]
    const L = Math.hypot(b[0] - a[0], b[1] - a[1])
    const k = Math.max(1, Math.ceil(L / 0.5))
    for (let j = i === 0 ? 0 : 1; j <= k; j++) out.push([a[0] + ((b[0] - a[0]) * j) / k, a[1] + ((b[1] - a[1]) * j) / k])
  }
  return out
}

/** the walls whose top reaches a joist band starting at zJoistBot (bearingWallUnder's test) */
function bearingCarriers(zJoistBot: number, walls: ResolvedElement[]): { id: string; pts: Pt2[]; reach: number }[] {
  const carriers: { id: string; pts: Pt2[]; reach: number }[] = []
  for (const w of walls) {
    if (w.category !== 'wall') continue
    const zr = nominalWallZ(w)
    if (!zr || zr.z1 < zJoistBot - 1 || zr.z1 > zJoistBot + 1.5 || zr.z0 > zJoistBot - 2) continue
    const path = w.params.path as Path2D | null
    if (!path?.nodes || path.nodes.length < 2) continue
    const pts = flattenPath(path)
    if (pts.length < 2) continue
    carriers.push({ id: w.id, pts, reach: Math.max(w.assemblyThicknessFt || 0, 0.3) + 0.1 })
  }
  return carriers
}

/**
 * STRUCTURAL PLANS (2026-10-21): the walls that CARRY a framing line — every floor or framed
 * ceiling's framing lines whose beam resolved to 'wall' (bearingWallUnder, or picked), and the
 * walls under at least 2′ of such a line. The Framing / Foundation Plan draws these black with
 * the exterior and concrete walls; every other wall stays in the screened background.
 */
export function framingBearingWallIds(elements: ResolvedElement[]): Set<string> {
  const out = new Set<string>()
  const walls = elements.filter((e) => e.category === 'wall')
  if (!walls.length || !clipperReady()) return out
  for (const el of elements) {
    if (el.category !== 'floor' && el.category !== 'ceiling') continue
    if (!framingSplitsOf(el.params).length) continue
    const band = el.category === 'ceiling' ? ceilingJoistBand(el) : floorJoistBand(el)
    if (!band) continue
    const holes = slabHoleRings(el)
    const w = floorJoistWidthIn(el.params) / 12
    const beams = seamBeams(el, band.ring, holes, w, band.zBot, band.zTop, el.category === 'floor' ? walls : [])
    const carriers = bearingCarriers(band.zBot, walls)
    if (!carriers.length) continue
    const piece: SlabPiece = { outer: signedArea(band.ring) >= 0 ? band.ring : [...band.ring].reverse(), holes }
    const splits = framingSplitsOf(el.params)
    for (const b of beams) {
      if (b.kind !== 'wall') continue
      const line = splits.find((x) => x.id === b.splitId)?.pts
      if (!line) continue
      const hits = new Map<string, number>()
      for (const p of lineSamples(line)) {
        if (!inPiece(piece, p)) continue
        for (const c of carriers) {
          if (c.pts.some((q, qi) => qi + 1 < c.pts.length && segDist(p, q, c.pts[qi + 1]) <= c.reach)) hits.set(c.id, (hits.get(c.id) || 0) + 1)
        }
      }
      for (const [id, n] of hits) if (n >= 4) out.add(id)
    }
  }
  return out
}

/** the derived beam under each framing line. Kind: the line's choice, else AUTO
 *  (bearingWallUnder → 'wall', else 'flush'). Size: the line's SEAM_BEAM_SIZES
 *  pick, else 2 plies of the joist (legacy `plies`/`depthIn` still honored). A
 *  flush beam's top is flush with the joists and it hangs below them when deeper;
 *  a dropped beam sits under the joists. Clipped to the floor. */
export function seamBeams(
  el: ResolvedElement, ring: Pt2[], holes: Pt2[][], w: number, zBot: number, zTop: number,
  walls: ResolvedElement[] = [],
): SeamBeam[] {
  const splits = framingSplitsOf(el.params)
  if (!splits.length) return []
  const joistDepthFt = zTop - zBot
  const outer = signedArea(ring) >= 0 ? ring : [...ring].reverse()
  const holesCw = holes.map((h) => (signedArea(h) <= 0 ? h : [...h].reverse()))
  const region = [outer, ...holesCw]
  const piece: SlabPiece = { outer, holes: holesCw }
  return splits.map((sp) => {
    const o = sp.beam || {}
    const chosen = o.kind === 'flush' || o.kind === 'dropped' || o.kind === 'wall' ? o.kind : null
    const kind = chosen ?? (bearingWallUnder(sp.pts, (p) => inPiece(piece, p), zBot, walls) ? 'wall' : 'flush')
    const sz = seamBeamSize(o.size)
    let widthFt: number, depthFt: number, size: string
    if (sz && sz.widthIn != null && sz.depthIn != null) {
      widthFt = sz.widthIn / 12; depthFt = sz.depthIn / 12; size = sz.value
    } else {
      const plies = sz?.plies ?? Math.max(1, Math.min(6, Math.round(Number(o.plies) || 2)))
      widthFt = plies * w
      depthFt = !sz && Number(o.depthIn) > 0 ? Number(o.depthIn) / 12 : joistDepthFt
      size = sz?.value ?? `joist${plies}`
    }
    const band = inflateOpen(sp.pts, widthFt / 2, 2)
    const rings = band.length ? intersectClosed(band, region).filter((r) => r.length >= 3 && signedArea(r) > 1e-9) : []
    const zHi = kind === 'dropped' ? zBot : zTop
    const zLo = zHi - depthFt
    return { splitId: sp.id, kind, auto: !chosen, widthFt, depthFt, size, rings, zLo, zHi }
  })
}

/** the seam beams as their own framing-class solids — one per framing line, so a 3D
 *  click can tell WHICH beam was hit (groupKey `…|framing-seam:<splitId>`). Nothing
 *  for a line that bears on a wall. */
export function buildSeamBeamSolids(el: ResolvedElement, walls: ResolvedElement[] = []): ElementSolid[] {
  if (!framingSplitsOf(el.params).length || !clipperReady()) return []
  const band = el.category === 'ceiling' ? ceilingJoistBand(el) : el.category === 'floor' ? floorJoistBand(el) : null
  if (!band) return []
  const w = floorJoistWidthIn(el.params) / 12
  const out: ElementSolid[] = []
  for (const b of seamBeams(el, band.ring, slabHoleRings(el), w, band.zBot, band.zTop, el.category === 'floor' ? walls : [])) {
    if (b.kind === 'wall' || !b.rings.length) continue
    const acc = newMeshAcc()
    const edges: number[] = []
    for (const r of b.rings) prism(acc, r, () => b.zLo, () => b.zHi, edges)
    if (acc.vc === 0) continue
    out.push({
      elementId: el.id,
      category: el.category as 'floor' | 'ceiling',
      outlines: [],
      zBase: b.zLo,
      zTop: b.zHi,
      materialId: framingMaterialId(el),
      groupKey: `${el.category}|${el.levelId}|${el.type.assemblyId ?? 'none'}|${el.id}|framing-seam:${b.splitId}`,
      pieces: [],
      placements: [],
      openingInstances: [],
      mesh: { positions: new Float32Array(acc.pos), normals: new Float32Array(acc.nrm), indices: new Uint32Array(acc.idx), edges, uvs: meshAccUvs(acc) },
      meshOnly: true,
      skipProjectedOutline: true,
      framing: true,
    })
  }
  return out
}

export function buildFloorFraming(el: ResolvedElement, walls: ResolvedElement[] = []): ElementSolid | null {
  if (!isWoodCoreFloor(el)) return null              // non-wood core: no framing derivation
  if (!clipperReady()) return null
  // same top-of-structure convention as slabSolid: level + offset is the TOP of the
  // full assembly; the joist (core) band sits below the "above" finish layers.
  // FRAMING SESSION Phase 4: when the assembly has no separate substrate layer
  // (the seeded "joists+sheathing" core lump), the top 3/4″ of the core IS the
  // sheathing — carved off the joist tops (floorJoistBand) so buildFloorSheathing's
  // deck can occupy it without coplanar z-fighting on the joist tops.
  const band = floorJoistBand(el)
  if (!band) return null
  const { ring, zBot, zTop } = band

  // FLOOR CORE DEFINITION round: spacing is an INSTANCE param now (the wall
  // studSpacingIn precedent) and joist plan width follows the declared family
  // (sawn 1½″, TJI flange 2½″) — see joistField, shared with framed ceilings
  const mesh = joistField(el, ring, zBot, zTop, walls)
  if (!mesh) return null

  return {
    elementId: el.id,
    category: 'floor',
    outlines: [],                      // plan linework untouched — the slab body draws the floor
    zBase: zBot,
    zTop,
    materialId: framingMaterialId(el),
    groupKey: `floor|${el.levelId}|${el.type.assemblyId ?? 'none'}|${el.id}|framing`,
    pieces: [],
    placements: [],
    openingInstances: [],
    mesh,
    meshOnly: true,
    skipProjectedOutline: true,
    framing: true,
  }
}
